import {
  type AdminResponse,
  type AuthResponse,
  type MfaRequiredResponse,
  type TwoFactorEnableResponse,
  type TwoFactorSetupResponse,
  type TwoFactorStatusResponse,
} from './types';
import { authRequest, clearTokens, getRefreshToken, request, setTokens } from './client';

// ─── backoffice-api auth client ────────────────────────────────────────────
//
// Thin fetch wrapper around backoffice-api's `/v1/auth/*` endpoints. There is
// no register endpoint — admins are created by a SUPERADMIN from the Admins
// page (Task 14e), never self-service. login() persists the token pair it
// receives via setTokens(); me() uses authRequest() to attach the access
// token as the Authorization header (transparently refreshing it if
// expired), and is also used by the `_app` route guard both to verify a
// stored session is still valid and to refresh the signed-in admin's current
// roles/active-derived access on every navigation into the shell.
//
// Task 6 (feature-029, 2FA): login() no longer unconditionally persists
// tokens — an admin with 2FA enabled gets back an `MfaRequiredResponse`
// (no tokens yet) instead of an `AuthResponse`, and the caller must complete
// the second factor via verifyTwoFactor() before any tokens exist.

export type LoginResponse = AuthResponse | MfaRequiredResponse;

export interface LoginInput {
  email: string;
  password: string;
}

export async function login(input: LoginInput): Promise<LoginResponse> {
  const data = await request<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if ('token' in data) {
    setTokens(data.token, data.refreshToken);
  }
  return data;
}

/** Second factor of login — exchanges the short-lived `mfaToken` plus a TOTP/backup code for real tokens. */
export async function verifyTwoFactor(mfaToken: string, code: string): Promise<AuthResponse> {
  const data = await request<AuthResponse>('/auth/verify-2fa', {
    method: 'POST',
    body: JSON.stringify({ mfaToken, code }),
  });
  setTokens(data.token, data.refreshToken);
  return data;
}

/** Starts 2FA enrollment for the signed-in admin — returns the QR code + manual-entry secret. */
export function setupTwoFactor(): Promise<TwoFactorSetupResponse> {
  return authRequest<TwoFactorSetupResponse>('/auth/2fa/setup', { method: 'POST' });
}

/** Confirms enrollment with a valid TOTP code and turns 2FA on — returns the 10 backup codes (shown once). */
export function enableTwoFactor(code: string): Promise<TwoFactorEnableResponse> {
  return authRequest<TwoFactorEnableResponse>('/auth/2fa/enable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** Turns 2FA off — requires re-entering the password plus one valid TOTP/backup code. */
export function disableTwoFactor(password: string, code: string): Promise<void> {
  return authRequest<void>('/auth/2fa/disable', {
    method: 'POST',
    body: JSON.stringify({ password, code }),
  });
}

export function getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  return authRequest<TwoFactorStatusResponse>('/auth/2fa/status');
}

export function me(): Promise<AdminResponse> {
  return authRequest<AdminResponse>('/auth/me');
}

/**
 * Clears local tokens immediately (synchronously, before the network call),
 * then best-effort revokes the refresh token server-side — sign-out must
 * never get stuck on a network failure, so any error here is swallowed.
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  clearTokens();
  if (!refreshToken) {
    return;
  }
  await request('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  }).catch(() => undefined);
}
